/**
 * Create Quiz Screen
 * Form to create a new live (Kahoot-style) quiz
 * 
 * Quizzes are ONLY for family weekends - no free play mode.
 * They are always team-based with team captains playing.
 */

import React, { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors, useTheme } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";

/**
 * Available icons for quiz
 */
const AVAILABLE_ICONS = [
  "help-circle",
  "bulb",
  "school",
  "book",
  "trophy",
  "star",
  "flash",
  "rocket",
  "planet",
  "musical-notes",
  "film",
  "football",
  "heart",
  "flag",
  "globe",
];

/**
 * Available colors for quiz
 */
const AVAILABLE_COLORS = [
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EC4899", // Pink
  "#6366F1", // Indigo
  "#14B8A6", // Teal
  "#EF4444", // Red
  "#84CC16", // Lime
  "#F97316", // Orange
];

export default function CreateQuizScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { isDark } = useTheme();
  const { activeProfile, activeWeekend } = useAuth();

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("help-circle");
  const [color, setColor] = useState("#3B82F6");
  const [timePerQuestion, setTimePerQuestion] = useState(30);
  const [pointsPerCorrect, setPointsPerCorrect] = useState(100);
  const [bonusSpeed, setBonusSpeed] = useState(50);
  const [randomize, setRandomize] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Calculate max points per question
   */
  const maxPointsPerQuestion = pointsPerCorrect + bonusSpeed;

  /**
   * Handle save quiz
   */
  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert("Fout", "Vul een naam in voor de quiz");
      return;
    }

    setIsSaving(true);

    try {
      const { data, error } = await supabase
        .from("quizzes")
        .insert({
          name: name.trim(),
          description: description.trim() || null,
          icon,
          color,
          weekend_id: activeWeekend?.id || null,
          time_per_question: timePerQuestion,
          points_per_correct: pointsPerCorrect,
          bonus_speed: bonusSpeed,
          randomize_questions: randomize,
          created_by: activeProfile?.id,
          is_active: true,
          is_weekend_enabled: false, // Admin needs to explicitly enable for weekend
          // Live quiz specific fields
          is_live_mode: true, // Always live mode for new quizzes
          is_team_based: true, // Always team-based
          allow_unlimited_plays: false, // Not applicable for live quiz
          max_plays_per_user: 1, // Only one live session
          max_plays_per_team: 1, // Only one player per team
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      // Navigate to the quiz questions page
      router.replace(`/(admin)/games/quiz/${data.id}/questions`);
    } catch (error) {
      console.error("Error creating quiz:", error);
      Alert.alert("Fout", "Kon quiz niet aanmaken");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        className="flex-1"
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <View className="flex-row items-center">
            <TouchableOpacity
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.surface }}
              accessibilityRole="button"
              accessibilityLabel="Sluiten"
            >
              <Ionicons name="close" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Nieuwe Quiz
            </Text>
          </View>

          <TouchableOpacity
            onPress={handleSave}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-xl"
            style={{ backgroundColor: isSaving ? colors.border : colors.primary }}
            accessibilityRole="button"
            accessibilityLabel="Quiz aanmaken"
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text className="font-bold text-white">Aanmaken</Text>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView
          className="flex-1 px-4"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Live Quiz Info Banner */}
          <View
            className="rounded-xl p-4 mb-6 flex-row items-start"
            style={{
              backgroundColor: isDark ? "#1E3A5F" : "#EFF6FF",
              borderWidth: 1,
              borderColor: isDark ? "#3B82F6" : "#BFDBFE",
            }}
          >
            <Ionicons name="tv-outline" size={24} color="#3B82F6" />
            <View className="ml-3 flex-1">
              <Text className="font-bold" style={{ color: isDark ? "#93C5FD" : "#1E40AF" }}>
                Live Quiz (Kahoot-stijl)
              </Text>
              <Text className="text-sm mt-1" style={{ color: isDark ? "#60A5FA" : "#3B82F6" }}>
                Deze quiz wordt live gespeeld tijdens het familieweekend. 
                Jij controleert de quiz vanaf je telefoon terwijl vragen op een 
                groot scherm getoond worden.
              </Text>
            </View>
          </View>

          {/* Preview */}
          <View className="items-center py-4">
            <View
              className="w-20 h-20 rounded-2xl items-center justify-center"
              style={{ backgroundColor: `${color}20` }}
            >
              <Ionicons name={icon as any} size={40} color={color} />
            </View>
            <Text className="mt-3 font-bold text-lg" style={{ color: colors.text }}>
              {name || "Quiz Naam"}
            </Text>
            {description && (
              <Text className="text-sm mt-1 text-center px-4" style={{ color: colors.textSecondary }}>
                {description}
              </Text>
            )}
          </View>

          {/* Name */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Naam *
            </Text>
            <TextInput
              value={name}
              onChangeText={setName}
              placeholder="Bijv. Algemene Kennis Quiz"
              placeholderTextColor={colors.textTertiary}
              className="rounded-xl px-4 py-3"
              style={{
                backgroundColor: colors.surface,
                color: colors.text,
              }}
              accessibilityLabel="Quiz naam"
            />
          </View>

          {/* Description */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Beschrijving (optioneel)
            </Text>
            <TextInput
              value={description}
              onChangeText={setDescription}
              placeholder="Korte beschrijving van de quiz"
              placeholderTextColor={colors.textTertiary}
              multiline
              numberOfLines={2}
              className="rounded-xl px-4 py-3"
              style={{
                backgroundColor: colors.surface,
                color: colors.text,
                minHeight: 70,
                textAlignVertical: "top",
              }}
              accessibilityLabel="Quiz beschrijving"
            />
          </View>

          {/* Icon Selection */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Icoon
            </Text>
            <View className="flex-row flex-wrap">
              {AVAILABLE_ICONS.map((iconName) => (
                <TouchableOpacity
                  key={iconName}
                  onPress={() => setIcon(iconName)}
                  className="w-12 h-12 rounded-xl items-center justify-center m-1"
                  style={{
                    backgroundColor: icon === iconName ? `${color}30` : colors.surface,
                    borderWidth: 2,
                    borderColor: icon === iconName ? color : colors.border,
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`Selecteer icoon ${iconName}`}
                  accessibilityState={{ selected: icon === iconName }}
                >
                  <Ionicons
                    name={iconName as any}
                    size={22}
                    color={icon === iconName ? color : colors.textSecondary}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Color Selection */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Kleur
            </Text>
            <View className="flex-row flex-wrap">
              {AVAILABLE_COLORS.map((colorOption) => (
                <TouchableOpacity
                  key={colorOption}
                  onPress={() => setColor(colorOption)}
                  className="w-12 h-12 rounded-xl items-center justify-center m-1"
                  style={{
                    backgroundColor: colorOption,
                    borderWidth: 3,
                    borderColor: color === colorOption ? colors.text : "transparent",
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`Selecteer kleur ${colorOption}`}
                  accessibilityState={{ selected: color === colorOption }}
                >
                  {color === colorOption && (
                    <Ionicons name="checkmark" size={20} color="#FFFFFF" />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Quiz Settings */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-3" style={{ color: colors.textSecondary }}>
              Instellingen
            </Text>

            <View
              className="rounded-xl p-4"
              style={{ backgroundColor: colors.surface }}
            >
              {/* Time per Question */}
              <View className="flex-row items-center justify-between mb-4">
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "#3B82F620" }}
                  >
                    <Ionicons name="time" size={20} color="#3B82F6" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text }}>Tijd per vraag</Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Seconden om te antwoorden
                    </Text>
                  </View>
                </View>
                <View className="flex-row items-center">
                  <TouchableOpacity
                    onPress={() => setTimePerQuestion(Math.max(10, timePerQuestion - 5))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    accessibilityLabel="Verminder tijd"
                  >
                    <Ionicons name="remove" size={18} color={colors.text} />
                  </TouchableOpacity>
                  <Text
                    className="mx-4 font-bold text-lg"
                    style={{ color: colors.text, minWidth: 40, textAlign: "center" }}
                  >
                    {timePerQuestion}s
                  </Text>
                  <TouchableOpacity
                    onPress={() => setTimePerQuestion(Math.min(120, timePerQuestion + 5))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    accessibilityLabel="Verhoog tijd"
                  >
                    <Ionicons name="add" size={18} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Points per Correct */}
              <View className="flex-row items-center justify-between mb-4">
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "#10B98120" }}
                  >
                    <Ionicons name="star" size={20} color="#10B981" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text }}>Basispunten</Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Punten voor goed antwoord
                    </Text>
                  </View>
                </View>
                <View className="flex-row items-center">
                  <TouchableOpacity
                    onPress={() => setPointsPerCorrect(Math.max(10, pointsPerCorrect - 10))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    accessibilityLabel="Verminder punten"
                  >
                    <Ionicons name="remove" size={18} color={colors.text} />
                  </TouchableOpacity>
                  <Text
                    className="mx-4 font-bold text-lg"
                    style={{ color: "#10B981", minWidth: 50, textAlign: "center" }}
                  >
                    {pointsPerCorrect}
                  </Text>
                  <TouchableOpacity
                    onPress={() => setPointsPerCorrect(Math.min(1000, pointsPerCorrect + 10))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    accessibilityLabel="Verhoog punten"
                  >
                    <Ionicons name="add" size={18} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Bonus Speed */}
              <View className="flex-row items-center justify-between mb-4">
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "#F59E0B20" }}
                  >
                    <Ionicons name="flash" size={20} color="#F59E0B" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text }}>Snelheidsbonus</Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Max bonus voor snelste antwoord
                    </Text>
                  </View>
                </View>
                <View className="flex-row items-center">
                  <TouchableOpacity
                    onPress={() => setBonusSpeed(Math.max(0, bonusSpeed - 10))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    accessibilityLabel="Verminder bonus"
                  >
                    <Ionicons name="remove" size={18} color={colors.text} />
                  </TouchableOpacity>
                  <Text
                    className="mx-4 font-bold text-lg"
                    style={{ color: "#F59E0B", minWidth: 50, textAlign: "center" }}
                  >
                    +{bonusSpeed}
                  </Text>
                  <TouchableOpacity
                    onPress={() => setBonusSpeed(Math.min(500, bonusSpeed + 10))}
                    className="w-8 h-8 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    accessibilityLabel="Verhoog bonus"
                  >
                    <Ionicons name="add" size={18} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Max Points Info */}
              <View
                className="p-3 rounded-lg flex-row items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="trophy" size={18} color="#F59E0B" />
                <Text className="ml-2 text-sm" style={{ color: colors.textSecondary }}>
                  Max {maxPointsPerQuestion} punten per vraag
                </Text>
              </View>
            </View>
          </View>

          {/* Additional Options */}
          <View className="mb-4">
            <View
              className="rounded-xl p-4"
              style={{ backgroundColor: colors.surface }}
            >
              {/* Randomize */}
              <TouchableOpacity
                onPress={() => setRandomize(!randomize)}
                className="flex-row items-center justify-between"
                accessibilityRole="switch"
                accessibilityState={{ checked: randomize }}
                accessibilityLabel="Vragen willekeurig volgorde"
              >
                <View className="flex-row items-center flex-1">
                  <View
                    className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                    style={{ backgroundColor: "#8B5CF620" }}
                  >
                    <Ionicons name="shuffle" size={20} color="#8B5CF6" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text }}>Willekeurige volgorde</Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Vragen in willekeurige volgorde tonen
                    </Text>
                  </View>
                </View>
                <View
                  className="w-14 h-8 rounded-full justify-center px-1"
                  style={{ backgroundColor: randomize ? "#8B5CF6" : colors.border }}
                >
                  <View
                    className="w-6 h-6 rounded-full bg-white"
                    style={{ alignSelf: randomize ? "flex-end" : "flex-start" }}
                  />
                </View>
              </TouchableOpacity>
            </View>
          </View>

          {/* How Live Quiz Works */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-3" style={{ color: colors.textSecondary }}>
              Hoe werkt het?
            </Text>
            <View
              className="rounded-xl p-4"
              style={{ backgroundColor: colors.surface }}
            >
              <HowItWorksStep
                number={1}
                title="Maak vragen"
                description="Voeg multiple-choice vragen toe met optioneel een afbeelding"
                colors={colors}
              />
              <HowItWorksStep
                number={2}
                title="Start de quiz"
                description="Teams joinen via de app, teamcaptains kiezen een mascotte"
                colors={colors}
              />
              <HowItWorksStep
                number={3}
                title="Speel live"
                description="Vragen op groot scherm, teams antwoorden op hun telefoon"
                colors={colors}
              />
              <HowItWorksStep
                number={4}
                title="Bekijk resultaten"
                description="Live ranking en uiteindelijke winnaar met animaties"
                colors={colors}
                isLast
              />
            </View>
          </View>

          <View className="h-24" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

/**
 * How It Works Step Component
 */
interface HowItWorksStepProps {
  number: number;
  title: string;
  description: string;
  colors: ReturnType<typeof useThemeColors>;
  isLast?: boolean;
}

function HowItWorksStep({ number, title, description, colors, isLast }: HowItWorksStepProps) {
  return (
    <View className={`flex-row ${isLast ? "" : "mb-4"}`}>
      <View
        className="w-8 h-8 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: colors.primaryLight }}
      >
        <Text className="font-bold" style={{ color: colors.primary }}>
          {number}
        </Text>
      </View>
      <View className="flex-1">
        <Text className="font-medium" style={{ color: colors.text }}>
          {title}
        </Text>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {description}
        </Text>
      </View>
    </View>
  );
}
